import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  LogOut,
  Mail,
  XCircle,
  ChevronDown,
  ChevronUp,
  Scale,
  Trash2,
} from "lucide-react";
import { api } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { LanguageSwitch } from "../components/LanguageSwitch";
import Logo from "../components/Logo";
import {
  normalizeCriteriaSections,
  getEligibilityFromCriteriaResult,
  unwrapCriteriaPayload,
} from "../utils/criteriaResult";

const CreditDashboardUploadPage = () => {
  const { logout, user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState("");
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [jobsLoadError, setJobsLoadError] = useState("");
  const [contractStatuses, setContractStatuses] = useState({});
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [notifyingJobs, setNotifyingJobs] = useState(new Set());
  const [toast, setToast] = useState({ message: null, type: 'success', hiding: false });
  const toastTimeoutRef = useRef(null);
  const toastFadeRef = useRef(null);
  const lastContractStatusCheckRef = useRef({});
  const lastCriteriaStatusCheckRef = useRef({});

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    if (toastFadeRef.current) clearTimeout(toastFadeRef.current);
    setToast({ message, type, hiding: false });
    toastTimeoutRef.current = setTimeout(() => {
      setToast((prev) => (prev.message ? { ...prev, hiding: true } : prev));
      toastFadeRef.current = setTimeout(() => {
        setToast({ message: null, type: 'success', hiding: false });
        toastTimeoutRef.current = null;
        toastFadeRef.current = null;
      }, 1000);
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      if (toastFadeRef.current) clearTimeout(toastFadeRef.current);
    };
  }, []);

  const normalizeJobStatus = (status) => {
    if (!status) return status;
    const normalized = status.toString().toLowerCase();
    return normalized === 'ready' ? 'done' : normalized;
  };

  // Check if email is verified - decode from token if available for latest status
  const getEmailVerifiedStatus = () => {
    // Try to get latest status from token first
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);
        return decoded.emailVerified === true;
      } catch (error) {
        console.error('Failed to decode token for email verification check:', error);
      }
    }
    
    // Fallback to user object
    if (user) {
      return user.emailVerified === true;
    }
    
    return false;
  };
  
  const isEmailVerified = getEmailVerifiedStatus();
  
  // Refresh user data only once on initial mount to get latest email verification status
  useEffect(() => {
    let isMounted = true;
    
    // Only refresh if we haven't checked recently (avoid unnecessary API calls)
    const lastCheck = sessionStorage.getItem('lastUserRefresh');
    const now = Date.now();
    const shouldRefresh = !lastCheck || (now - parseInt(lastCheck)) > 60000; // Refresh if never checked or more than 1 minute ago
    
    if (refreshUser && shouldRefresh && isMounted) {
      refreshUser().then(() => {
        if (isMounted) {
          sessionStorage.setItem('lastUserRefresh', now.toString());
        }
      });
    }
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    loadJobs();
    // Poll for job updates every 5 seconds
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount and poll
  }, []);

  const loadJobs = async () => {
    try {
      const response = await api.get('/jobs');
      const jobsData = response.data.jobs || [];
      console.log('Raw jobs data from API:', jobsData);
      // Debug: log status of jobs that might show as "In Analyse" (first 15 by list order)
      const summary = jobsData.slice(0, 15).map((j) => ({ id: j.jobId?.slice(0, 8), filename: j.filename, status: j.status, created: j.createdAt }));
      console.log('[Jobs] First 15 from API (status should match DB):', summary);

      // Transform API jobs to match component format
      const transformedJobs = jobsData.map((job) => {
        console.log(`Job ${job.jobId}: contractId=${job.contractId}, groupId=${job.groupId}, status=${job.status}`);
        console.log(`Job ${job.jobId} - resultJson:`, job.resultJson);
        console.log(`Job ${job.jobId} - resultHuman:`, job.resultHuman);
        return {
          id: job.jobId,
          filename: job.filename || `Job ${job.jobId.substring(0, 8)}`,
          createdAt: new Date(job.createdAt).toLocaleString(),
          status: job.status === 'done' ? 'done' : 
                 job.status === 'failed' ? 'error' : 
                 job.status === 'processing' ? 'processing' : 
                 job.status === 'lawyer_notified' ? 'lawyer_notified' : 'queued',
          confidence: job.confidence,
          contractId: job.contractId,
          groupId: job.groupId, // Add groupId for list_of_contracts
          criteriaName: job.criteriaName || 'Final_test_copy_16',
          criteriaAppliedAt: job.criteriaAppliedAt || null,
          resultJson: job.resultJson,
          resultHuman: job.resultHuman,
          lawyerNotifiedAt: job.lawyerNotifiedAt ? new Date(job.lawyerNotifiedAt) : null,
        };
      });
      
      setJobs(transformedJobs);

      const nowMs = Date.now();

      // Check criteria results status for jobs with contractId
      const enableCriteriaStatusChecks = true;
      if (enableCriteriaStatusChecks) {
        const criteriaStatusCheckIntervalMs = 5 * 60 * 1000;
        const skippedDoneOrFailed = transformedJobs.filter(j => j.status === 'done' || j.status === 'error').length;
        if (skippedDoneOrFailed > 0) {
          console.log(`[Jobs] Trusting DB status for ${skippedDoneOrFailed} done/failed job(s); not re-checking via API`);
        }
        const jobsToCheck = transformedJobs.filter(job => {
          if (!job.contractId || !job.groupId) {
            return false;
          }
          // Don't re-check jobs that are already done, failed, or lawyer_notified (trust DB; avoid overwriting with stale API)
          if (job.status === 'done' || job.status === 'error' || job.status === 'lawyer_notified') {
            return false;
          }
          const statusKey = job.contractId || job.id;
          const existingStatus = contractStatuses[statusKey] || contractStatuses[job.id] || null;
          const normalizedStatus = normalizeJobStatus(existingStatus);
          const hasResults = !!job.resultJson || !!job.resultHuman;
          if (normalizedStatus === 'done' || normalizedStatus === 'failed') {
            return !hasResults;
          }
          return true;
        });

        const fetchCriteriaStatusForJob = async (job) => {
          const lastCheckMs = lastCriteriaStatusCheckRef.current[job.id] || 0;
          if (nowMs - lastCheckMs < criteriaStatusCheckIntervalMs) {
            return null;
          }

          try {
            lastCriteriaStatusCheckRef.current[job.id] = nowMs;
            const token = localStorage.getItem('token');
            const statusResponse = await api.post(
              '/api/check_contract_criteria_status',
              {
                contract_id: job.contractId,
                criteria_name: job.criteriaName || 'Final_test_copy_16',
                group_id: job.groupId,
              },
              token ? { headers: { Authorization: `Bearer ${token}` } } : {}
            );
            const apiStatus = normalizeJobStatus(statusResponse.data.status);
            return {
              jobId: job.id,
              contractId: job.contractId,
              status: apiStatus,
            };
          } catch (err) {
            console.error(`Failed to check criteria status for job ${job.id} (${job.filename}):`, err);
            return null;
          }
        };

        if (jobsToCheck.length > 0) {
          const statuses = [];
          const maxConcurrent = 3;
          for (let i = 0; i < jobsToCheck.length; i += maxConcurrent) {
            const batch = jobsToCheck.slice(i, i + maxConcurrent);
            const batchStatuses = await Promise.all(batch.map(fetchCriteriaStatusForJob));
            statuses.push(...batchStatuses.filter(Boolean));
            if (i + maxConcurrent < jobsToCheck.length) {
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }

          if (statuses.length > 0) {
            const statusMap = {};
            const updatedJobs = transformedJobs.map(job => {
              // Never change status for jobs that are already done or failed (trust DB)
              if (job.status === 'done' || job.status === 'error') {
                return job;
              }
              const statusInfo = statuses.find(s => s.jobId === job.id);
              if (!statusInfo || !statusInfo.status) {
                return job;
              }
              const nextStatus =
                job.status === 'lawyer_notified'
                  ? 'lawyer_notified'
                  : statusInfo.status === 'done'
                    ? 'done'
                    : statusInfo.status === 'failed'
                      ? 'failed'
                      : 'processing';
              statusMap[statusInfo.contractId || statusInfo.jobId] = statusInfo.status;
              statusMap[statusInfo.jobId] = statusInfo.status;
              return {
                ...job,
                status: nextStatus,
              };
            });
            setJobs(updatedJobs);
            setContractStatuses((prev) => ({ ...prev, ...statusMap }));
          }
        }
      }

      // list_of_contracts checks disabled to avoid API spamming
      const enableContractListChecks = false;
      if (!enableContractListChecks) {
        return;
      }

      // Check contract statuses using list_of_contracts endpoint
      // Use groupId and filename to get status directly from the list
      console.log('Checking contract statuses using list_of_contracts...');

      const lastGlobalCheck = sessionStorage.getItem('lastContractStatusGlobalCheck');
      const lastGlobalCheckMs = lastGlobalCheck ? parseInt(lastGlobalCheck, 10) : 0;
      const nowMsList = Date.now();
      const shouldCheckGlobal = !lastGlobalCheckMs || nowMsList - lastGlobalCheckMs >= 5 * 60 * 1000;
      if (!shouldCheckGlobal) {
        return;
      }
      const jobsToCheck = transformedJobs.filter(job => {
        // Only check jobs that have groupId (needed for list_of_contracts)
        const hasGroupId = !!job.groupId;
        if (!hasGroupId) {
          console.log(`Job ${job.id} has no groupId, skipping status check via list_of_contracts`);
        }
        return hasGroupId;
      });

      const fetchStatusForJob = async (job) => {
        try {
          const lastCheckMs = lastContractStatusCheckRef.current[job.id] || 0;
          const shouldCheck = nowMs - lastCheckMs >= 5 * 60 * 1000;
          if (!shouldCheck) {
            return {
              jobId: job.id,
              contractId: job.contractId,
              status: job.status,
            };
          }

          // Use the new list_of_contracts endpoint with filename and groupId
          // Remove file extension from filename since API stores name without extension
          const contractName = job.filename.replace(/\.[^/.]+$/, '');
          const statusResponse = await api.post('/api/get_contract_status_by_list', {
            contractName: contractName,
            groupId: job.groupId,
          });
          console.log(`Job ${job.id} (${job.filename}) - list_of_contracts response:`, statusResponse.data);
          const apiStatus = statusResponse.data.status;
          const normalizedStatus = normalizeJobStatus(apiStatus);
          const retrievedContractId = statusResponse.data.contractId || job.contractId;
          console.log(`Job ${job.id} - status from list: ${apiStatus}, normalized: ${normalizedStatus}, contractId: ${retrievedContractId}`);
          lastContractStatusCheckRef.current[job.id] = nowMs;
          
          return {
            jobId: job.id,
            contractId: retrievedContractId,
            status: normalizedStatus,
          };
        } catch (err) {
          const statusCode = err?.response?.status;
          if (statusCode === 404) {
            // Contract may not be available in the list yet; treat as pending
            return {
              jobId: job.id,
              contractId: job.contractId,
              status: job.status,
            };
          }
          console.error(`Failed to check status for job ${job.id} (${job.filename}):`, err);
          // For failed API calls, use database status
          return {
            jobId: job.id,
            contractId: job.contractId,
            status: job.status,
          };
        }
      };

      const statuses = [];
      const maxConcurrent = 3;
      for (let i = 0; i < jobsToCheck.length; i += maxConcurrent) {
        const batch = jobsToCheck.slice(i, i + maxConcurrent);
        const batchStatuses = await Promise.all(batch.map(fetchStatusForJob));
        statuses.push(...batchStatuses);
        if (i + maxConcurrent < jobsToCheck.length) {
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }
      const statusMap = {};
      // Also update jobs with contractId if it was retrieved
      const updatedJobs = transformedJobs.map(job => {
        const statusInfo = statuses.find(s => s.jobId === job.id);
        if (statusInfo && statusInfo.contractId && !job.contractId) {
          // Update job with contractId if it was retrieved
          return { ...job, contractId: statusInfo.contractId };
        }
        return job;
      });
      
      // Update jobs state with contractIds
      setJobs(updatedJobs);
      
      statuses.forEach(({ jobId, contractId, status }) => {
        // Map by contractId if available, otherwise by jobId
        // Use both keys to ensure lookup works regardless of when contractId is set
        const contractKey = contractId || jobId;
        const jobKey = jobId;
        
        // Store status under both keys for reliable lookup
        statusMap[contractKey] = status;
        if (contractId && contractKey !== jobKey) {
          statusMap[jobKey] = status; // Also store under jobId for backward compatibility
        }
        
        console.log(`Setting contractStatuses[${contractKey}] = ${status} (jobId: ${jobId}, contractId: ${contractId})`);
      });
      
      console.log('Final contractStatuses map:', statusMap);
      setContractStatuses(statusMap);
      sessionStorage.setItem('lastContractStatusGlobalCheck', nowMsList.toString());
      setJobsLoadError("");
    } catch (err) {
      console.error('Failed to load jobs:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Network error";
      setJobsLoadError(t('userPage.errorLoadJobs') + (msg ? ' ' + msg : ''));
    } finally {
      setLoadingJobs(false);
    }
  };

  const inferContentType = (file) => {
    if (file?.type) return file.type;
    const extension = file?.name?.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return 'application/pdf';
    if (extension === 'zip') return 'application/zip';
    return '';
  };

  const allowedTypes = ['application/pdf', 'application/zip', 'application/x-zip-compressed'];
  const allowedExtensions = ['pdf', 'zip'];
  const isAllowedFile = (file) => {
    if (allowedTypes.includes(file.type)) return true;
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    return allowedExtensions.includes(extension);
  };

  const addFiles = (fileArray) => {
    if (!fileArray?.length) return;
    const maxFiles = 10;
    const next = selectedFiles.length + fileArray.length > maxFiles
      ? [...selectedFiles, ...fileArray].slice(0, maxFiles)
      : [...selectedFiles, ...fileArray];
    const invalidFiles = next.filter(file => !isAllowedFile(file));
    if (invalidFiles.length > 0) {
      setError(t('userPage.errorInvalidType') + ' ' + invalidFiles.map(f => f.name).join(', '));
      return;
    }
    const maxSize = 50 * 1024 * 1024; // 50MB
    const oversizedFiles = next.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      setError(t('userPage.errorFileTooBig') + ' ' + oversizedFiles.map(f => f.name).join(', '));
      return;
    }
    if (next.length > maxFiles) {
      setError(t('userPage.errorMaxFiles').replace(/\{\{max\}\}/g, String(maxFiles)));
    }
    setSelectedFiles(next);
    setError("");
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (!files) return;
    const fileArray = Array.from(files);
    if (fileArray.length > 10) {
      setError(t('userPage.errorMaxFiles').replace(/\{\{max\}\}/g, '10'));
      setSelectedFiles(fileArray.slice(0, 10));
      return;
    }
    const invalidFiles = fileArray.filter(file => !isAllowedFile(file));
    if (invalidFiles.length > 0) {
      setError(t('userPage.errorInvalidType') + ' ' + invalidFiles.map(f => f.name).join(', '));
      return;
    }
    const maxSize = 50 * 1024 * 1024;
    const oversizedFiles = fileArray.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      setError(t('userPage.errorFileTooBig') + ' ' + oversizedFiles.map(f => f.name).join(', '));
      return;
    }
    setSelectedFiles(fileArray);
    setError("");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEmailVerified) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (!isEmailVerified) return;
    const files = e.dataTransfer.files;
    if (!files?.length) return;
    addFiles(Array.from(files));
  };

  const handleUpload = async () => {
    // Block upload if email is not verified
    if (!isEmailVerified) {
      setError(t('userPage.errorVerifyEmail'));
      return;
    }
    
    // Block upload if no files or no consent
    if (!selectedFiles.length || !consent) return;

    setUploading(true);
    setError("");

    try {
      // Process each file sequentially to avoid overwhelming the API
      const uploadedJobs = [];

      for (const file of selectedFiles) {
        try {
          const inferredContentType = inferContentType(file) || 'application/pdf';

          // Get presigned URL then upload file directly to S3 (bucket CORS must allow your origin)
          let presignedUrl, s3Key;
          try {
            const presignedResponse = await api.post('/presigned-url', {
              filename: file.name,
              contentType: inferredContentType,
            });
            presignedUrl = presignedResponse.data.presignedUrl;
            s3Key = presignedResponse.data.s3Key;
          } catch (e) {
            const m = e.response?.data?.error || e.response?.data?.message || e.message || '';
            throw new Error(m ? `Upload-Link: ${m}` : 'Verbindung zum Server fehlgeschlagen. Bitte später erneut versuchen.');
          }

          try {
            const s3Host = presignedUrl ? new URL(presignedUrl).hostname : '(no URL)';
            console.log('[Upload] Step 1: presigned URL received. PUT will go to:', s3Host, 'Key:', s3Key);
          } catch (_) { /* ignore URL parse */ }

          let uploadResponse;
          try {
            console.log('[Upload] Step 2: sending PUT to S3...');
            uploadResponse = await fetch(presignedUrl, {
              method: 'PUT',
              mode: 'cors',
              body: file,
              headers: { 'Content-Type': inferredContentType },
            });
          } catch (e) {
            const m = (e && e.message) || 'Failed to fetch';
            if (m === 'Failed to fetch' || m.includes('NetworkError'))
              throw new Error('Speicher-Upload fehlgeschlagen (oft CORS/Netzwerk). Bitte CORS für den Vertrags-S3-Bucket prüfen oder Support kontaktieren.');
            throw new Error(`Speicher-Upload: ${m}`);
          }
          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            console.error('[Upload] Step 2 FAILED: S3 PUT returned', uploadResponse.status, errorText?.slice(0, 200));
            throw new Error(`Upload fehlgeschlagen (${uploadResponse.status}): ${errorText || uploadResponse.statusText}`);
          }

          console.log('[Upload] Step 2 OK: file is in S3.');
          // Log exact S3 location for verification (bucket from presigned URL hostname)
          try {
            const putUrl = new URL(presignedUrl);
            const bucketFromUrl = putUrl.hostname.split('.')[0];
            console.log('[Upload] S3 OK. Bucket:', bucketFromUrl, 'Key:', s3Key, 'Full path: s3://' + bucketFromUrl + '/' + s3Key);
          } catch (_) { /* ignore URL parse */ }

          // Create document record
          const documentResponse = await api.post('/documents', {
            s3Key,
            originalFilename: file.name,
            mimeType: inferredContentType,
          });

          const { documentId } = documentResponse.data;

          // Step 4: Create analysis job - send only s3Key so backend downloads from S3 (avoids 413 from API Gateway 10MB limit)
          const token = localStorage.getItem('token');
          const jobResponse = await api.post(
            '/jobs',
            {
              documentId,
              filename: file.name,
              contentType: inferredContentType,
              s3Key,
            },
            token ? { headers: { Authorization: `Bearer ${token}` } } : {}
          );

          let analysisStatus = normalizeJobStatus(jobResponse.data.status || 'processing');

          uploadedJobs.push({
            id: jobResponse.data.jobId,
            filename: file.name,
            createdAt: new Date().toLocaleString(),
            status: analysisStatus === 'done' ? 'done' : 
                   analysisStatus === 'failed' ? 'error' : 
                   analysisStatus === 'processing' ? 'processing' : 'queued',
          });
        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          const errorMessage = fileError.response?.data?.error || 
                              fileError.response?.data?.message ||
                              fileError.message || 
                              t('userPage.errorUnknown');
          setError(
            t('userPage.errorUploadFile').replace('{{name}}', file.name) + ': ' + errorMessage
          );
          // Continue with other files even if one fails
        }
      }

      // Reset form
      setSelectedFiles([]);
      setConsent(false);
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';

      // Reload jobs to get updated list
      await loadJobs();
    } catch (err) {
      console.error('Upload error:', err);
      setError(
        err.response?.data?.error || err.message || t('userPage.errorUploadFailed')
      );
    } finally {
      setUploading(false);
    }
  };

  const [jobDetails, setJobDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  const fetchJobDetails = async (jobId) => {
    if (loadingDetails[jobId]) return;
    setLoadingDetails((prev) => ({ ...prev, [jobId]: true }));
    try {
      const response = await api.get(`/jobs/${jobId}`);
      console.log(`Fetched job details for ${jobId}:`, response.data);
      setJobDetails((prev) => ({
        ...prev,
        [jobId]: {
          resultJson: response.data.resultJson || response.data.result,
          resultHuman: response.data.resultHuman,
        },
      }));
    } catch (error) {
      console.error(`Failed to fetch details for job ${jobId}:`, error);
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [jobId]: false }));
    }
  };


  const toggleJobExpansion = async (jobId) => {
    const isCurrentlyExpanded = expandedJobs.has(jobId);
    
    setExpandedJobs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });

    // Fetch job details if expanding and missing results
    const existingDetails = jobDetails[jobId];
    const missingResults = !existingDetails || (!existingDetails.resultJson && !existingDetails.resultHuman);
    if (!isCurrentlyExpanded && missingResults) {
      await fetchJobDetails(jobId);
    }
  };

  const getCriteriaStatus = (job) => {
    const contractKey = job.contractId || job.id;
    const jobKey = job.id;
    const apiStatus = contractStatuses[contractKey] || contractStatuses[jobKey] || null;
    return normalizeJobStatus(apiStatus);
  };

  const getDisplayStatus = (job) => {
    // Trust DB status when already done or failed (no GG API call needed to display it)
    if (job.status === 'done' || job.status === 'error') {
      return job.status;
    }
    if (job.status === 'lawyer_notified') {
      return 'lawyer_notified';
    }

    const criteriaStatus = getCriteriaStatus(job);
    const hasCriteriaApplied = !!job.criteriaAppliedAt;

    const displayStatus = !job.contractId
      ? job.status
      : (!hasCriteriaApplied
          ? job.status === 'processing' ? 'processing' : job.status
          : (criteriaStatus === 'done'
              ? 'done'
              : criteriaStatus === 'failed'
                ? 'failed'
                : criteriaStatus || job.status));

    if (job.contractId) {
      console.log(
        `Job ${job.id} (contract ${job.contractId || 'none'}): displayStatus=${displayStatus}, criteriaStatus=${criteriaStatus}, jobStatus=${job.status}`
      );
    }

    return displayStatus;
  };

  const getStatusPill = (job) => {
    const base =
      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium";

    const normalizedStatus = getDisplayStatus(job);

    switch (normalizedStatus) {
      case "done":
        return (
          <span
            className={`${base} bg-emerald-50 text-emerald-700 border border-emerald-200`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {t('userPage.statusDone')}
          </span>
        );
      case "lawyer_notified":
        return (
          <span
            className={`${base} bg-blue-50 text-blue-700 border border-blue-200`}
          >
            <Scale className="w-3.5 h-3.5" />
            {t('userPage.statusLawyerNotified')}
          </span>
        );
      case "processing":
        return (
          <span
            className={`${base} bg-sky-50 text-sky-700 border border-sky-200`}
          >
            <Clock className="w-3.5 h-3.5" />
            {t('userPage.statusProcessing')}
          </span>
        );
      case "error":
      case "failed":
        return (
          <span
            className={`${base} bg-rose-50 text-rose-700 border border-rose-200`}
          >
            <AlertCircle className="w-3.5 h-3.5" />
            {t('userPage.statusError')}
          </span>
        );
      case "queued":
      case "pending":
        return (
          <span
            className={`${base} bg-amber-50 text-amber-700 border border-amber-200`}
          >
            <Clock className="w-3.5 h-3.5" />
            {t('userPage.statusQueued')}
          </span>
        );
      default:
        return (
          <span
            className={`${base} bg-amber-50 text-amber-700 border border-amber-200`}
          >
            <Clock className="w-3.5 h-3.5" />
            {t('userPage.statusQueued')}
          </span>
        );
    }
  };

  const hasJobs = jobs.length > 0;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 h-12 flex items-center justify-between gap-4">
          <Logo asLink to="/" />
          <div className="flex items-center gap-3 text-sm">
            <LanguageSwitch />
            <span className="hidden sm:inline text-slate-600">
              {user?.email || 'test@test.test'}
            </span>
            {!isEmailVerified && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 border border-amber-200">
                <XCircle className="w-3.5 h-3.5" />
                {t('common.emailNotVerified')}
              </span>
            )}
            <button 
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-md bg-rose-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            >
              <LogOut className="w-3.5 h-3.5" />
              {t('common.logout')}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-10 space-y-6">
          {/* Email verification warning */}
          {!isEmailVerified && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 md:p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-amber-900 mb-1">
                    Email Verification Required
                  </h3>
                  <p className="text-sm text-amber-800 mb-3">
                    Please verify your email address before uploading documents. Check your inbox for the verification email, or click the button below to resend it.
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        // Try to get email from user object or localStorage
                        const storedUser = localStorage.getItem('user');
                        const userEmail = user?.email || (storedUser ? JSON.parse(storedUser).email : null);
                        
                        // Send request with email if available
                        const payload = userEmail ? { email: userEmail } : {};
                        await api.post('/auth/resend-verification', payload);
                        showToast(t('userPage.toastVerificationSent'), 'success');
                      } catch (error) {
                        console.error('Resend verification error:', error);
                        showToast(error.response?.data?.error || error.message || t('userPage.toastVerificationFailed'), 'error');
                      }
                    }}
                    className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {t('verifyEmail.resend')}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Page intro */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-1">
                {t('userPage.pageTitle')}
              </h2>
              <p className="text-sm text-slate-600 max-w-2xl">
                {t('userPage.pageIntro')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-3 py-1 font-medium">
                {t('userPage.badgeGdpr')}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 border border-emerald-200">
                {t('userPage.badgeResult')}
              </span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Upload card */}
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 md:p-6 space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    {t('userPage.dropzoneTitle')}
                  </h3>
                 
                </div>
                <div className="hidden sm:flex flex-col items-end text-[11px] text-slate-500">
                  <span>{t('userPage.allowedFormats')}</span>
                  <span>{t('userPage.maxSize')}</span>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)] items-stretch">
                {/* Dropzone-style area: click to select or drag and drop */}
                <label
                  className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${
                    isEmailVerified
                      ? isDragOver
                        ? 'cursor-pointer border-emerald-400 bg-emerald-50/80'
                        : 'cursor-pointer border-slate-300 bg-slate-50/60 hover:border-slate-400 hover:bg-slate-50'
                      : 'cursor-not-allowed border-slate-200 bg-slate-100/60 opacity-60'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    onChange={handleFileChange}
                    accept=".pdf,.zip"
                    disabled={!isEmailVerified}
                  />
                  <UploadCloud className="w-8 h-8 text-slate-500 mb-3" />
                  <p className="text-sm font-medium text-slate-800">
                    {t('userPage.dropzoneHint')}
                  </p>
                  <p className="mt-1 text-[11px] text-slate-500">
                    {t('userPage.multipleDocs')}
                  </p>
                </label>

                {/* Helper checklist */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-xs text-slate-700 space-y-2">
                  <p className="font-semibold text-slate-900 mb-1">
                    {t('userPage.bestResultTitle')}
                  </p>
                  <ul className="space-y-1 list-disc list-outside pl-4">
                    <li>{t('userPage.bestResult1')}</li>
                    <li>{t('userPage.bestResult2')}</li>
                    <li>{t('userPage.bestResult3')}</li>
                    <li>{t('userPage.bestResult4')}</li>
                  </ul>
                 
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>{error}</p>
                  </div>
                </div>
              )}

              {/* Selected files preview */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-slate-700 uppercase tracking-[0.18em]">
                    {t('userPage.selectedFiles')}
                  </p>
                  {selectedFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFiles([]);
                        setError("");
                      }}
                      className="text-[11px] text-slate-500 hover:text-slate-700"
                    >
                      {t('userPage.resetSelection')}
                    </button>
                  )}
                </div>

                {selectedFiles.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    {t('userPage.noFilesSelected')}
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-slate-50/80 text-xs">
                    {selectedFiles.map((file, idx) => {
                      const sizeKB = file.size / 1024;
                      const sizeLabel =
                        sizeKB > 1024
                          ? `${(sizeKB / 1024).toFixed(1)} MB`
                          : `${Math.max(1, Math.round(sizeKB))} KB`;
                      return (
                        <li
                          key={idx}
                          className="flex items-center justify-between gap-3 px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-white border border-slate-200">
                              <FileText className="w-3.5 h-3.5 text-slate-500" />
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-[11px] font-medium text-slate-800">
                                {file.name}
                              </p>
                              <p className="text-[10px] text-slate-500">
                                {sizeLabel}
                              </p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Consent + Upload button */}
              <div className="space-y-3">
                {/* Consent checkbox */}
                <div className="flex items-start gap-2 text-[11px] text-slate-600">
                  <input
                    id="consent"
                    type="checkbox"
                    className="mt-[3px] h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <label
                    htmlFor="consent"
                    className="leading-snug cursor-pointer"
                  >
                    {t('userPage.consentLabel')}{' '}
                    <a
                      href="https://www.kolrus.com/datenschutz/"
                      className="underline underline-offset-2 ml-1"
                    >
                      {t('userPage.consentLink')}
                    </a>
                    
                  </label>
                </div>

                {/* Button row */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <p className="text-[11px] text-slate-500 max-w-sm">
                    {t('userPage.uploadDisclaimer')}
                  </p>
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={!isEmailVerified || !selectedFiles.length || !consent || uploading}
                    className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 ${
                      isEmailVerified && selectedFiles.length && consent && !uploading
                        ? "bg-slate-900 text-white hover:bg-slate-800 focus-visible:ring-slate-900"
                        : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                  >
                    {uploading ? t('userPage.uploading') : t('userPage.uploadButton')}
                  </button>
                </div>
              </div>
            </section>

            {/* Which documents should I upload? */}
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 md:p-6 space-y-3">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">
                {t('userPage.documentsTitle')}
              </h3>
              <p className="text-xs text-slate-600 max-w-3xl">
                {t('userPage.documentsIntro')}
              </p>
              <ul className="space-y-1 list-disc list-inside text-xs text-slate-700">
                <li>{t('userPage.docList1')}</li>
                <li>{t('userPage.docList2')}</li>
                <li>{t('userPage.docList3')}</li>
                <li>{t('userPage.docList4')}</li>
                <li>{t('userPage.docList5')}</li>
                <li>{t('userPage.docList6')}</li>
              </ul>
              <p className="text-[11px] text-slate-500">
                {t('userPage.documentsNote')}
              </p>
            </section>

            {/* Jobs list below */}
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 md:p-6 space-y-4">
              <div className="flex items-center justify-between gap-3 mb-1">
                <h3 className="text-lg font-semibold text-slate-900">
                  {t('userPage.jobsTitle')}
                </h3>
                <span className="text-[11px] text-slate-500">
                  {jobs.length} {jobs.length === 1 ? t('userPage.jobCountOne') : t('userPage.jobCountMany')}
                </span>
              </div>
              {jobsLoadError ? (
                <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                  {jobsLoadError}
                </div>
              ) : null}
              {loadingJobs ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-8 text-center">
                  <Clock className="w-8 h-8 text-slate-300 mb-3 animate-spin" />
                  <p className="text-sm font-medium text-slate-700 mb-1">
                    {t('userPage.loadingJobs')}
                  </p>
                </div>
              ) : !hasJobs ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-8 text-center">
                  <FileText className="w-8 h-8 text-slate-300 mb-3" />
                  <p className="text-sm font-medium text-slate-700 mb-1">
                    {t('userPage.noJobs')}
                  </p>
                  <p className="text-xs text-slate-500 max-w-xs">
                    {t('userPage.noJobsHint')}
                  </p>
                </div>
              ) : (
                <ul className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                  {jobs.map((job) => {
                    const isExpanded = expandedJobs.has(job.id);
                    const criteriaStatus = getCriteriaStatus(job);
                    const criteriaReady = criteriaStatus === 'done';
                    const criteriaFailed = criteriaStatus === 'failed';
                    const normalizedStatus = getDisplayStatus(job);
                    
                    // Get result data from job or fetched details
                    const jobDetail = jobDetails[job.id] || {};
                    const resultJson = jobDetail.resultJson || job.resultJson;
                    const resultHuman = jobDetail.resultHuman || job.resultHuman;

                    const criteriaSections = normalizeCriteriaSections(resultJson);
                    const { resultNode } = unwrapCriteriaPayload(resultJson);
                    const hasCriteriaContent =
                      (Array.isArray(criteriaSections) && criteriaSections.length > 0) ||
                      (resultNode != null && String(resultNode).trim() !== '');
                    const displayResultHuman = (resultHuman && String(resultHuman).trim()) ? String(resultHuman).trim() : null;

                    return (
                      <li
                        key={job.id}
                        className="rounded-xl border border-slate-200 bg-white overflow-hidden transition-all hover:shadow-sm"
                      >
                        {/* Job Header - Clickable */}
                        <button
                          onClick={() => toggleJobExpansion(job.id)}
                          className="w-full px-4 py-3 flex items-start justify-between gap-3 hover:bg-slate-50 transition-colors text-left"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-slate-900 truncate">
                              {job.filename}
                            </p>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              {t('userPage.createdAt')} {job.createdAt}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {getStatusPill(job)}
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        </button>

                        {/* Expandable Content */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50/50">
                            {(criteriaReady || normalizedStatus === 'done' || normalizedStatus === 'lawyer_notified') ? (
                              <div className="pt-4 space-y-4">
                                {/* Analysis Results */}
                                {displayResultHuman ? (
                                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                                    <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                                      {displayResultHuman}
                                    </div>
                                  </div>
                                ) : null}

                                {/* Eligibility from result_node (API) or legacy key questions */}
                                {!hasCriteriaContent && resultJson && (
                                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                                    <p className="text-xs text-amber-800">
                                      {t('userPage.criteriaLoadError')}
                                    </p>
                                  </div>
                                )}
                                {hasCriteriaContent ? (() => {
                                  const { eligible, notEligibleReasons, manualReviewRequired } = getEligibilityFromCriteriaResult(resultJson);
                                  return (
                                    <div className={`rounded-lg border p-4 ${
                                      eligible === false
                                        ? 'border-rose-200 bg-rose-50'
                                        : eligible === null || manualReviewRequired
                                          ? 'border-amber-200 bg-amber-50'
                                          : 'border-emerald-200 bg-emerald-50'
                                    }`}>
                                      <h4 className="text-xs font-semibold text-slate-900 mb-2">
                                        {t('userPage.eligibilityHeading')}
                                      </h4>
                                      {eligible === false ? (
                                        <>
                                          <p className="text-sm font-medium text-rose-800">
                                            {t('userPage.eligibilityNoClaim')}
                                          </p>
                                          {notEligibleReasons.length > 0 && (
                                            <p className="text-xs text-rose-700 mt-2">
                                              {t('userPage.eligibilityReason')}
                                            </p>
                                          )}
                                          <ul className="mt-1 list-disc list-inside text-xs text-rose-700 space-y-0.5">
                                            {notEligibleReasons.map((reason, i) => (
                                              <li key={i}>{reason}</li>
                                            ))}
                                          </ul>
                                        </>
                                      ) : eligible === null || manualReviewRequired ? (
                                        <p className="text-sm font-medium text-amber-800">
                                          {t('userPage.eligibilityManualReview')}
                                        </p>
                                      ) : (
                                        <p className="text-sm font-medium text-emerald-800">
                                          {t('userPage.eligibilityPossibleClaim')}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })() : null}

                                {/* Action Buttons */}
                                <div className="flex flex-col gap-2">
                                  {/* Proceed with Lawyer Button */}
                                  {(() => {
                                    // Check if lawyer was notified recently (within 24 hours)
                                    const COOLDOWN_HOURS = 24;
                                    const isInCooldown = job.lawyerNotifiedAt && (() => {
                                      const now = new Date();
                                      const notifiedAt = job.lawyerNotifiedAt;
                                      const hoursSinceNotification = (now.getTime() - notifiedAt.getTime()) / (1000 * 60 * 60);
                                      return hoursSinceNotification < COOLDOWN_HOURS;
                                    })();
                                    
                                    const remainingHours = job.lawyerNotifiedAt && isInCooldown
                                      ? Math.ceil(COOLDOWN_HOURS - (new Date().getTime() - job.lawyerNotifiedAt.getTime()) / (1000 * 60 * 60))
                                      : null;
                                    
                                    const isNotifying = notifyingJobs.has(job.id);
                                    const isButtonDisabled = (job.status !== 'done' && job.status !== 'lawyer_notified') || isInCooldown || isNotifying;
                                    
                                    return (
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            setNotifyingJobs((prev) => {
                                              const next = new Set(prev);
                                              next.add(job.id);
                                              return next;
                                            });
                                            await api.post('/api/notify-lawyer', {
                                              jobId: job.id,
                                            });
                                                                            const now = new Date();
                                            // Optimistically update status so UI shows "Anwalt benachrichtigt" immediately
                                            setJobs((prevJobs) =>
                                              prevJobs.map((j) =>
                                                j.id === job.id
                                                  ? {
                                                      ...j,
                                                      status: 'lawyer_notified',
                                                      lawyerNotifiedAt: now,
                                                    }
                                                  : j
                                              )
                                            );
                                            showToast(t('userPage.toastLawyerNotified'), 'success');
                                            // Refetch after a short delay so the server list returns the updated status (avoids race with any in-flight poll)
                                            await new Promise((r) => setTimeout(r, 300));
                                            await loadJobs();
                                          } catch (error) {
                                            console.error('Failed to notify lawyer:', error);
                                            showToast(error.response?.data?.error || t('userPage.toastLawyerNotifyError'), 'error');
                                          } finally {
                                            setNotifyingJobs((prev) => {
                                              const next = new Set(prev);
                                              next.delete(job.id);
                                              return next;
                                            });
                                          }
                                        }}
                                        disabled={isButtonDisabled}
                                        className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-xs font-medium transition-colors shadow-sm ${
                                          isButtonDisabled
                                            ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                        }`}
                                      >
                                        <Scale className="w-3.5 h-3.5" />
                                        {isNotifying
                                          ? t('userPage.notifyLawyer')
                                          : isInCooldown
                                            ? t('userPage.notifyLawyerCooldown').replace('{{h}}', remainingHours)
                                            : t('userPage.assertClaims')
                                        }
                                      </button>
                                    );
                                  })()}

                                  {/* Delete Button */}
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!window.confirm(t('userPage.deleteConfirm'))) {
                                        return;
                                      }
                                      try {
                                        await api.post('/api/delete-job', {
                                          jobId: job.id,
                                        });
                                        // Remove job from local state and reload
                                        setJobs((prevJobs) => prevJobs.filter((j) => j.id !== job.id));
                                        setExpandedJobs((prev) => {
                                          const newSet = new Set(prev);
                                          newSet.delete(job.id);
                                          return newSet;
                                        });
                                        showToast(t('userPage.deleteSuccess'), 'success');
                                      } catch (error) {
                                        console.error('Failed to delete job:', error);
                                        showToast(error.response?.data?.error || t('userPage.deleteError'), 'error');
                                      }
                                    }}
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-xs font-medium text-rose-700 hover:bg-rose-100 transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    {t('userPage.deleteContract')}
                                  </button>
                                </div>
                              </div>
                            ) : normalizedStatus === "processing" || normalizedStatus === "queued" ? (
                              <div className="pt-4 space-y-3">
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                                  <div className="flex items-start gap-3">
                                    <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <h4 className="text-xs font-semibold text-amber-900 mb-1">
                                        {t('userPage.analysisRunning')}
                                      </h4>
                                      <p className="text-[11px] text-amber-800">
                                        {t('userPage.analysisRunningDesc')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Delete Button */}
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!window.confirm(t('userPage.deleteConfirm'))) {
                                      return;
                                    }
                                    try {
                                      await api.post('/api/delete-job', {
                                        jobId: job.id,
                                      });
                                      setJobs((prevJobs) => prevJobs.filter((j) => j.id !== job.id));
                                      setExpandedJobs((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.delete(job.id);
                                        return newSet;
                                      });
                                      showToast(t('userPage.deleteSuccess'), 'success');
                                    } catch (error) {
                                      console.error('Failed to delete job:', error);
                                      showToast(error.response?.data?.error || t('userPage.deleteError'), 'error');
                                    }
                                  }}
                                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-xs font-medium text-rose-700 hover:bg-rose-100 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  {t('userPage.deleteContract')}
                                </button>
                              </div>
                            ) : normalizedStatus === "error" || normalizedStatus === "failed" || criteriaFailed ? (
                              <div className="pt-4 space-y-3">
                                <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                                  <div className="flex items-start gap-3">
                                    <AlertCircle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <h4 className="text-xs font-semibold text-rose-900 mb-1">
                                        {t('userPage.analysisFailed')}
                                      </h4>
                                      <p className="text-[11px] text-rose-800 mb-3">
                                        {t('userPage.analysisFailedDesc')}
                                      </p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // TODO: Implement retry or contact support
                                          showToast(t('userPage.retryButton'), 'success');
                                        }}
                                        className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-rose-700 transition-colors"
                                      >
                                        {t('userPage.retryButton')}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Delete Button */}
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!window.confirm(t('userPage.deleteConfirm'))) {
                                      return;
                                    }
                                    try {
                                      await api.post('/api/delete-job', {
                                        jobId: job.id,
                                      });
                                      setJobs((prevJobs) => prevJobs.filter((j) => j.id !== job.id));
                                      setExpandedJobs((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.delete(job.id);
                                        return newSet;
                                      });
                                      showToast(t('userPage.deleteSuccess'), 'success');
                                    } catch (error) {
                                      console.error('Failed to delete job:', error);
                                      showToast(error.response?.data?.error || t('userPage.deleteError'), 'error');
                                    }
                                  }}
                                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-xs font-medium text-rose-700 hover:bg-rose-100 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  {t('userPage.deleteContract')}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="text-[11px] text-slate-500">
                {t('userPage.emailNote')}
              </p>
            </section>

            {/* Next steps / cost & cancellation info */}
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 md:p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">
                {t('userPage.nextSteps.title')}
              </h3>
              <p className="text-xs text-slate-700 leading-relaxed">
                {t('userPage.nextSteps.p1')}
              </p>
              <p className="text-xs text-slate-700 leading-relaxed">
                {t('userPage.nextSteps.p2')}
              </p>
              <p className="text-sm font-medium text-slate-900 mt-3">
                {t('userPage.nextSteps.subtitle')}
              </p>
              <p className="text-xs text-slate-700 leading-relaxed">
                {t('userPage.nextSteps.p3')}
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* In-app toast: auto-dismiss after 5s with 1s fade-out */}
      {toast.message && (
        <div
          role="alert"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md rounded-lg px-4 py-3 text-sm font-medium shadow-lg transition-opacity duration-1000 ${
            toast.hiding ? 'opacity-0' : 'opacity-100'
          } ${
            toast.type === 'error'
              ? 'bg-rose-600 text-white'
              : 'bg-emerald-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default CreditDashboardUploadPage;
